package com.example.borshi.web.nutritionist.ui

import androidx.compose.runtime.*
import com.example.borshi.web.nutritionist.models.*
import com.example.borshi.web.nutritionist.repository.NutritionistRepository
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Modal for assigning a nutrition plan to a patient
 * Creates user_personalized_plans and initializes subscription
 */
@Composable
fun AssignPlanModal(
    isOpen: Boolean,
    repository: NutritionistRepository,
    nutritionistId: String,
    preSelectedPatientId: String? = null,
    onClose: () -> Unit,
    onPlanAssigned: () -> Unit
) {
    if (!isOpen) return

    // State
    var patients by remember { mutableStateOf<List<Patient>>(emptyList()) }
    var plans by remember { mutableStateOf<List<NutritionistBasePlan>>(emptyList()) }
    var selectedPatientId by remember { mutableStateOf(preSelectedPatientId ?: "") }
    var selectedPlanId by remember { mutableStateOf("") }
    var startDate by remember { mutableStateOf("") }
    var deliveryDays by remember { mutableStateOf(setOf("Monday", "Friday")) }
    var deliveryTime by remember { mutableStateOf("morning") }
    var customNotes by remember { mutableStateOf("") }

    // Customization state
    var planCustomizations by remember { mutableStateOf<Map<String, PlanItemCustomization>>(emptyMap()) }
    var showCustomization by remember { mutableStateOf(false) }

    var isLoading by remember { mutableStateOf(true) }
    var isSubmitting by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var currentStep by remember { mutableStateOf(1) } // 1=Select Patient/Plan, 2=Customize (optional), 3=Schedule, 4=Review

    val scope = rememberCoroutineScope()

    // Load data
    LaunchedEffect(isOpen) {
        if (isOpen) {
            scope.launch {
                isLoading = true

                // Load patients and plans in parallel
                val patientsResult = repository.getPatients(nutritionistId)
                val plansResult = repository.getBasePlans(nutritionistId)

                patientsResult.onSuccess { patients = it }
                plansResult.onSuccess { plans = it.filter { it.isActive } }

                isLoading = false
            }
        }
    }

    // Get selected objects
    val selectedPatient = remember(selectedPatientId, patients) {
        patients.find { it.patientId == selectedPatientId }
    }

    val selectedPlan = remember(selectedPlanId, plans) {
        plans.find { it.basePlanId == selectedPlanId }
    }

    // Modal Overlay
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(0,0,0,0.5)"))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
            padding(20.px)
        }
        onClick { onClose() }
    }) {
        // Modal Content
        Div({
            style {
                backgroundColor(Color.white)
                borderRadius(12.px)
                maxWidth(800.px)
                width(100.percent)
                maxHeight(90.vh)
                overflowY("auto")
                property("box-shadow", "0 4px 20px rgba(0,0,0,0.3)")
            }
            onClick { it.stopPropagation() }
        }) {
            // Header
            Div({
                style {
                    padding(20.px)
                    property("border-bottom", "1px solid #eee")
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        marginBottom(16.px)
                    }
                }) {
                    H2({ style { margin(0.px); fontSize(24.px); color(Color("#333")) } }) {
                        Text("🎯 Assign Plan to Patient")
                    }
                    Button({
                        style {
                            backgroundColor(Color("transparent"))
                            border(0.px)
                            fontSize(24.px)
                            cursor("pointer")
                            padding(0.px)
                            width(32.px)
                            height(32.px)
                        }
                        onClick { onClose() }
                    }) {
                        Text("×")
                    }
                }

                // Step Indicator
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(12.px)
                    }
                }) {
                    StepIndicator(1, currentStep, "Select")
                    StepIndicator(2, currentStep, "Customize")
                    StepIndicator(3, currentStep, "Schedule")
                    StepIndicator(4, currentStep, "Confirm")
                }
            }

            // Content
            Div({ style { padding(20.px) } }) {
                if (error != null) {
                    ErrorMessage(error!!)
                }

                when {
                    isLoading -> LoadingSpinner()
                    else -> {
                        when (currentStep) {
                            1 -> Step1SelectPatientPlan(
                                patients, selectedPatientId, { selectedPatientId = it },
                                plans, selectedPlanId, { selectedPlanId = it },
                                preSelectedPatientId != null
                            )
                            2 -> Step2CustomizePlan(
                                selectedPlan,
                                planCustomizations,
                                { planCustomizations = it }
                            )
                            3 -> Step3Schedule(
                                startDate, { startDate = it },
                                deliveryDays, { deliveryDays = it },
                                deliveryTime, { deliveryTime = it },
                                customNotes, { customNotes = it }
                            )
                            4 -> Step4Review(
                                selectedPatient, selectedPlan, startDate,
                                deliveryDays, deliveryTime, customNotes,
                                planCustomizations
                            )
                        }
                    }
                }
            }

            // Footer
            Div({
                style {
                    padding(20.px)
                    property("border-top", "1px solid #eee")
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                }
            }) {
                Button({
                    style {
                        padding(10.px, 20.px)
                        backgroundColor(Color("transparent"))
                        border(1.px, LineStyle.Solid, Color("#ddd"))
                        borderRadius(6.px)
                        cursor("pointer")
                        fontSize(14.px)
                    }
                    onClick {
                        if (currentStep > 1) currentStep-- else onClose()
                    }
                }) {
                    Text(if (currentStep > 1) "← Back" else "Cancel")
                }

                Button({
                    style {
                        padding(10.px, 20.px)
                        backgroundColor(if (isSubmitting) Color("#ccc") else Color("#4CAF50"))
                        color(Color.white)
                        border(0.px)
                        borderRadius(6.px)
                        cursor(if (isSubmitting) "not-allowed" else "pointer")
                        fontSize(14.px)
                        fontWeight("600")
                    }
                    if (isSubmitting) attr("disabled", "disabled")
                    onClick {
                        when (currentStep) {
                            1 -> {
                                if (selectedPatientId.isBlank() || selectedPlanId.isBlank()) {
                                    error = "Please select both patient and plan"
                                } else {
                                    error = null
                                    currentStep++
                                }
                            }
                            2 -> {
                                // Customization step - always valid, just move forward
                                error = null
                                currentStep++
                            }
                            3 -> {
                                if (startDate.isBlank()) {
                                    error = "Please select start date"
                                } else if (deliveryDays.isEmpty()) {
                                    error = "Please select at least one delivery day"
                                } else {
                                    error = null
                                    currentStep++
                                }
                            }
                            4 -> {
                                // Assign plan
                                scope.launch {
                                    isSubmitting = true
                                    error = null

                                    try {
                                        console.log("🎯 Assigning plan $selectedPlanId to patient $selectedPatientId")
                                        console.log("   Start date: $startDate")
                                        console.log("   Delivery days: ${deliveryDays.joinToString()}")
                                        console.log("   Delivery time: $deliveryTime")
                                        console.log("   Notes: $customNotes")
                                        console.log("   Customizations: ${planCustomizations.size} items modified")

                                        repository.assignPlanToPatient(
                                            patientId = selectedPatientId,
                                            basePlanId = selectedPlanId,
                                            startDate = startDate,
                                            deliveryDays = deliveryDays,
                                            deliveryTime = deliveryTime,
                                            customNotes = customNotes
                                        ).onSuccess { personalizedPlanId ->
                                            console.log("✅ Plan assigned successfully! Personalized plan ID: $personalizedPlanId")
                                            onPlanAssigned()
                                            onClose()
                                        }.onFailure { exception ->
                                            error = "Failed to assign plan: ${exception.message}"
                                            isSubmitting = false
                                        }
                                    } catch (e: Exception) {
                                        error = "Error: ${e.message}"
                                        isSubmitting = false
                                    }
                                }
                            }
                        }
                    }
                }) {
                    Text(when {
                        isSubmitting -> "Assigning..."
                        currentStep < 4 -> "Next →"
                        else -> "Assign Plan"
                    })
                }
            }
        }
    }
}

// Step 1: Select Patient and Plan
@Composable
private fun Step1SelectPatientPlan(
    patients: List<Patient>,
    selectedPatientId: String,
    onPatientSelect: (String) -> Unit,
    plans: List<NutritionistBasePlan>,
    selectedPlanId: String,
    onPlanSelect: (String) -> Unit,
    patientPreSelected: Boolean
) {
    FormSection("Select Patient and Plan") {
        // Patient Selection
        FormField("Patient *", "patient") {
            if (patientPreSelected && selectedPatientId.isNotBlank()) {
                val patient = patients.find { it.patientId == selectedPatientId }
                Div({
                    style {
                        padding(12.px)
                        backgroundColor(Color("#E8F5E9"))
                        borderRadius(6.px)
                        border(1.px, LineStyle.Solid, Color("#4CAF50"))
                    }
                }) {
                    Text("✓ ${patient?.name ?: "Selected Patient"}")
                }
            } else {
                Select({
                    style {
                        width(100.percent)
                        padding(10.px)
                        border(1.px, LineStyle.Solid, Color("#ddd"))
                        borderRadius(6.px)
                        fontSize(14.px)
                    }
                    onChange { onPatientSelect(it.value ?: "") }
                }) {
                    Option("", { if (selectedPatientId.isBlank()) attr("selected", "selected") }) {
                        Text("-- Select a patient --")
                    }
                    patients.forEach { patient ->
                        Option(patient.patientId, {
                            if (selectedPatientId == patient.patientId) attr("selected", "selected")
                        }) {
                            Text("${patient.name} (${patient.age} yrs, ${patient.gender})")
                        }
                    }
                }
            }
        }

        // Plan Selection
        FormField("Nutrition Plan *", "plan") {
            Select({
                style {
                    width(100.percent)
                    padding(10.px)
                    border(1.px, LineStyle.Solid, Color("#ddd"))
                    borderRadius(6.px)
                    fontSize(14.px)
                }
                onChange { onPlanSelect(it.value ?: "") }
            }) {
                Option("", { if (selectedPlanId.isBlank()) attr("selected", "selected") }) {
                    Text("-- Select a plan --")
                }
                plans.forEach { plan ->
                    Option(plan.basePlanId, {
                        if (selectedPlanId == plan.basePlanId) attr("selected", "selected")
                    }) {
                        Text("${plan.planName} (৳${plan.pricingTemplate.weeklyBase.toInt()}/week)")
                    }
                }
            }
        }

        // Show plan preview if selected
        plans.find { it.basePlanId == selectedPlanId }?.let { plan ->
            Div({
                style {
                    marginTop(16.px)
                    padding(16.px)
                    backgroundColor(Color("#f8f9fa"))
                    borderRadius(8.px)
                }
            }) {
                Div({ style { fontSize(16.px); fontWeight("600"); marginBottom(8.px) } }) {
                    Text("📋 ${plan.planName}")
                }
                Div({ style { fontSize(14.px); color(Color("#666")); marginBottom(12.px) } }) {
                    Text(plan.description)
                }
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "repeat(3, 1fr)")
                        gap(12.px)
                    }
                }) {
                    PlanMetric("Protein", "${plan.nutritionalTargets.proteinPerWeek}g/wk")
                    PlanMetric("Calories", "${plan.nutritionalTargets.caloriesPerDay} kcal/day")
                    PlanMetric("Price", "৳${plan.pricingTemplate.weeklyBase.toInt()}/wk")
                }
            }
        }
    }
}

// Step 2: Customize Plan (NEW)
@Composable
private fun Step2CustomizePlan(
    selectedPlan: NutritionistBasePlan?,
    planCustomizations: Map<String, PlanItemCustomization>,
    onCustomizationsChange: (Map<String, PlanItemCustomization>) -> Unit
) {
    FormSection("Customize Plan (Optional)") {
        if (selectedPlan == null) {
            Div({
                style {
                    padding(40.px)
                    textAlign("center")
                    color(Color("#999"))
                }
            }) {
                Text("⚠️ No plan selected")
            }
            return@FormSection
        }

        // Info message
        Div({
            style {
                backgroundColor(Color("#E3F2FD"))
                padding(12.px)
                borderRadius(6.px)
                marginBottom(20.px)
                fontSize(14.px)
                color(Color("#1976D2"))
            }
        }) {
            Text("💡 You can adjust quantities or remove items based on patient needs. Changes will be saved with the personalized plan.")
        }

        // Show plan's recommended products
        if (selectedPlan.recommendedProducts.isEmpty()) {
            Div({
                style {
                    padding(40.px)
                    textAlign("center")
                    color(Color("#999"))
                }
            }) {
                Text("This plan has no specific product recommendations yet. You can skip this step.")
            }
        } else {
            Div({ style { marginBottom(12.px); fontWeight("600"); fontSize(14.px) } }) {
                Text("Recommended Products (${selectedPlan.recommendedProducts.size})")
            }

            selectedPlan.recommendedProducts.forEach { product ->
                val customization = planCustomizations[product.productId]
                val currentQuantity = customization?.customQuantity ?: product.quantityPerWeek
                val isRemoved = customization?.isRemoved ?: false

                Div({
                    style {
                        backgroundColor(if (isRemoved) Color("#FFEBEE") else Color("#f8f9fa"))
                        padding(16.px)
                        borderRadius(8.px)
                        marginBottom(12.px)
                        opacity(if (isRemoved) 0.6 else 1.0)
                        property("transition", "all 0.3s ease")
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.SpaceBetween)
                            alignItems(AlignItems.Start)
                            marginBottom(12.px)
                        }
                    }) {
                        Div({ style { flex(1) } }) {
                            Div({ style { fontSize(16.px); fontWeight("600"); marginBottom(4.px) } }) {
                                Text("${product.productName} ${if (isRemoved) "(Removed)" else ""}")
                            }
                            Div({ style { fontSize(12.px); color(Color("#666")) } }) {
                                Text("Priority: ${product.priority.name} • ${product.frequency}")
                            }
                            if (product.nutritionBenefit.isNotBlank()) {
                                Div({ style { fontSize(12.px); color(Color("#2196F3")); marginTop(4.px) } }) {
                                    Text("💡 ${product.nutritionBenefit}")
                                }
                            }
                        }

                        // Remove toggle button
                        Button({
                            style {
                                padding(6.px, 12.px)
                                backgroundColor(if (isRemoved) Color("#4CAF50") else Color("#F44336"))
                                color(Color.white)
                                border(0.px)
                                borderRadius(4.px)
                                cursor("pointer")
                                fontSize(12.px)
                                fontWeight("600")
                            }
                            onClick {
                                val newCustomizations = planCustomizations.toMutableMap()
                                val current = newCustomizations[product.productId] ?: PlanItemCustomization(
                                    productId = product.productId,
                                    productName = product.productName,
                                    originalQuantity = product.quantityPerWeek,
                                    customQuantity = product.quantityPerWeek
                                )
                                newCustomizations[product.productId] = current.copy(isRemoved = !isRemoved)
                                onCustomizationsChange(newCustomizations)
                            }
                        }) {
                            Text(if (isRemoved) "Restore" else "Remove")
                        }
                    }

                    if (!isRemoved) {
                        // Quantity adjuster
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(12.px)
                            }
                        }) {
                            Span({ style { fontSize(14.px); color(Color("#666")) } }) {
                                Text("Quantity/Week:")
                            }

                            // Decrease button
                            Button({
                                style {
                                    width(32.px)
                                    height(32.px)
                                    backgroundColor(Color("#f5f5f5"))
                                    border(1.px, LineStyle.Solid, Color("#ddd"))
                                    borderRadius(4.px)
                                    cursor("pointer")
                                    fontSize(18.px)
                                    fontWeight("bold")
                                }
                                onClick {
                                    if (currentQuantity > 100) {
                                        val newCustomizations = planCustomizations.toMutableMap()
                                        val current = newCustomizations[product.productId] ?: PlanItemCustomization(
                                            productId = product.productId,
                                            productName = product.productName,
                                            originalQuantity = product.quantityPerWeek,
                                            customQuantity = product.quantityPerWeek
                                        )
                                        newCustomizations[product.productId] = current.copy(
                                            customQuantity = currentQuantity - 100
                                        )
                                        onCustomizationsChange(newCustomizations)
                                    }
                                }
                            }) {
                                Text("-")
                            }

                            // Display current quantity
                            Div({
                                style {
                                    padding(8.px, 16.px)
                                    backgroundColor(Color.white)
                                    border(1.px, LineStyle.Solid, Color("#ddd"))
                                    borderRadius(4.px)
                                    fontSize(16.px)
                                    fontWeight("600")
                                    minWidth(80.px)
                                    textAlign("center")
                                }
                            }) {
                                Text("${currentQuantity}g")
                            }

                            // Increase button
                            Button({
                                style {
                                    width(32.px)
                                    height(32.px)
                                    backgroundColor(Color("#f5f5f5"))
                                    border(1.px, LineStyle.Solid, Color("#ddd"))
                                    borderRadius(4.px)
                                    cursor("pointer")
                                    fontSize(18.px)
                                    fontWeight("bold")
                                }
                                onClick {
                                    val newCustomizations = planCustomizations.toMutableMap()
                                    val current = newCustomizations[product.productId] ?: PlanItemCustomization(
                                        productId = product.productId,
                                        productName = product.productName,
                                        originalQuantity = product.quantityPerWeek,
                                        customQuantity = product.quantityPerWeek
                                    )
                                    newCustomizations[product.productId] = current.copy(
                                        customQuantity = currentQuantity + 100
                                    )
                                    onCustomizationsChange(newCustomizations)
                                }
                            }) {
                                Text("+")
                            }

                            // Show change indicator
                            if (currentQuantity != product.quantityPerWeek) {
                                val change = currentQuantity - product.quantityPerWeek
                                Span({
                                    style {
                                        fontSize(12.px)
                                        color(if (change > 0) Color("#4CAF50") else Color("#F44336"))
                                        fontWeight("600")
                                    }
                                }) {
                                    Text("(${if (change > 0) "+" else ""}${change}g)")
                                }
                            }
                        }
                    }
                }
            }
        }

        // Summary of changes
        val changedItems = planCustomizations.filter { (_, custom) ->
            custom.isRemoved || custom.customQuantity != custom.originalQuantity
        }

        if (changedItems.isNotEmpty()) {
            Div({
                style {
                    backgroundColor(Color("#FFF3E0"))
                    padding(12.px)
                    borderRadius(6.px)
                    marginTop(16.px)
                    fontSize(14.px)
                }
            }) {
                Div({ style { fontWeight("600"); marginBottom(8.px); color(Color("#E65100")) } }) {
                    Text("📝 Changes Summary:")
                }
                changedItems.forEach { (_, custom) ->
                    Div({ style { fontSize(12.px); marginBottom(4.px) } }) {
                        if (custom.isRemoved) {
                            Text("• Removed: ${custom.productName}")
                        } else {
                            val change = custom.customQuantity - custom.originalQuantity
                            Text("• ${custom.productName}: ${custom.originalQuantity}g → ${custom.customQuantity}g (${if (change > 0) "+" else ""}${change}g)")
                        }
                    }
                }
            }
        }
    }
}

// Step 3: Schedule Setup
@Composable
private fun Step3Schedule(
    startDate: String,
    onStartDateChange: (String) -> Unit,
    deliveryDays: Set<String>,
    onDeliveryDaysChange: (Set<String>) -> Unit,
    deliveryTime: String,
    onDeliveryTimeChange: (String) -> Unit,
    customNotes: String,
    onNotesChange: (String) -> Unit
) {
    FormSection("Delivery Schedule") {
        // Start Date
        FormField("Start Date *", "startDate") {
            Input(org.jetbrains.compose.web.attributes.InputType.Date) {
                value(startDate)
                onInput { onStartDateChange(it.value) }
                style {
                    width(100.percent)
                    padding(10.px)
                    border(1.px, LineStyle.Solid, Color("#ddd"))
                    borderRadius(6.px)
                    fontSize(14.px)
                }
            }
        }

        // Delivery Days
        FormField("Delivery Days *", "days") {
            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "repeat(4, 1fr)")
                    gap(8.px)
                }
            }) {
                listOf("Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday").forEach { day ->
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(8.px)
                            padding(8.px)
                            border(1.px, LineStyle.Solid, if (deliveryDays.contains(day)) Color("#4CAF50") else Color("#ddd"))
                            borderRadius(6.px)
                            backgroundColor(if (deliveryDays.contains(day)) Color("#E8F5E9") else Color.white)
                            fontSize(13.px)
                        }
                    }) {
                        Input(org.jetbrains.compose.web.attributes.InputType.Checkbox) {
                            checked(deliveryDays.contains(day))
                            onChange {
                                val newDays = if (it.value) {
                                    deliveryDays + day
                                } else {
                                    deliveryDays - day
                                }
                                onDeliveryDaysChange(newDays)
                            }
                        }
                        Span({ style { cursor("pointer") } }) {
                            Text(day.take(3))
                        }
                    }
                }
            }
        }

        // Delivery Time
        FormField("Delivery Time", "time") {
            Select({
                style {
                    width(100.percent)
                    padding(10.px)
                    border(1.px, LineStyle.Solid, Color("#ddd"))
                    borderRadius(6.px)
                    fontSize(14.px)
                }
                onChange { onDeliveryTimeChange(it.value ?: "morning") }
            }) {
                Option("morning", { if (deliveryTime == "morning") attr("selected", "selected") }) {
                    Text("Morning (8 AM - 12 PM)")
                }
                Option("afternoon", { if (deliveryTime == "afternoon") attr("selected", "selected") }) {
                    Text("Afternoon (12 PM - 4 PM)")
                }
                Option("evening", { if (deliveryTime == "evening") attr("selected", "selected") }) {
                    Text("Evening (4 PM - 8 PM)")
                }
            }
        }

        // Custom Notes
        FormField("Notes (Optional)", "notes") {
            TextArea(attrs = {
                value(customNotes)
                onInput { onNotesChange(it.value) }
                attr("placeholder", "Any special instructions or notes...")
                attr("rows", "3")
                style {
                    width(100.percent)
                    padding(10.px)
                    border(1.px, LineStyle.Solid, Color("#ddd"))
                    borderRadius(6.px)
                    fontSize(14.px)
                    fontFamily("inherit")
                }
            })
        }
    }
}

// Step 4: Review Assignment
@Composable
private fun Step4Review(
    patient: Patient?,
    plan: NutritionistBasePlan?,
    startDate: String,
    deliveryDays: Set<String>,
    deliveryTime: String,
    notes: String,
    planCustomizations: Map<String, PlanItemCustomization>
) {
    FormSection("Review Assignment") {
        // Patient Info
        Div({
            style {
                padding(16.px)
                backgroundColor(Color("#f8f9fa"))
                borderRadius(8.px)
                marginBottom(16.px)
            }
        }) {
            Div({ style { fontSize(14.px); color(Color("#666")); marginBottom(8.px) } }) {
                Text("Patient")
            }
            Div({ style { fontSize(18.px); fontWeight("600"); color(Color("#333")) } }) {
                Text(patient?.name ?: "Unknown")
            }
            Div({ style { fontSize(14.px); color(Color("#666")); marginTop(4.px) } }) {
                Text("${patient?.age} years, ${patient?.gender}")
            }
        }

        // Plan Info
        Div({
            style {
                padding(16.px)
                backgroundColor(Color("#E3F2FD"))
                borderRadius(8.px)
                marginBottom(16.px)
            }
        }) {
            Div({ style { fontSize(14.px); color(Color("#666")); marginBottom(8.px) } }) {
                Text("Nutrition Plan")
            }
            Div({ style { fontSize(18.px); fontWeight("600"); color(Color("#1976D2")) } }) {
                Text(plan?.planName ?: "Unknown")
            }
            Div({ style { fontSize(14.px); color(Color("#666")); marginTop(4.px) } }) {
                Text("৳${plan?.pricingTemplate?.weeklyBase?.toInt() ?: 0} per week")
            }
        }

        // Schedule Info
        ReviewItem("Start Date", startDate)
        ReviewItem("Delivery Days", deliveryDays.joinToString(", "))
        ReviewItem("Delivery Time", deliveryTime.replaceFirstChar { it.uppercase() })

        if (notes.isNotBlank()) {
            ReviewItem("Notes", notes)
        }

        // Success Message
        Div({
            style {
                backgroundColor(Color("#E8F5E9"))
                padding(16.px)
                borderRadius(8.px)
                marginTop(20.px)
                property("border-left", "4px solid #4CAF50")
            }
        }) {
            P({ style { margin(0.px); fontSize(14.px); color(Color("#2E7D32")); fontWeight("600") } }) {
                Text("✓ Ready to assign plan!")
            }
            P({ style { margin(8.px, 0.px, 0.px); fontSize(13.px); color(Color("#666")) } }) {
                Text("The patient will be notified and can start their plan from ${startDate}.")
            }
        }
    }
}

// Helper Components
@Composable
private fun StepIndicator(step: Int, currentStep: Int, label: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
            opacity(if (step <= currentStep) 1.0 else 0.4)
        }
    }) {
        Div({
            style {
                width(32.px)
                height(32.px)
                borderRadius(50.percent)
                backgroundColor(if (step <= currentStep) Color("#4CAF50") else Color("#ddd"))
                color(Color.white)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                fontWeight("600")
            }
        }) {
            Text(step.toString())
        }
        Span({ style { fontSize(14.px); fontWeight(if (step == currentStep) "600" else "normal") } }) {
            Text(label)
        }
    }
}

@Composable
private fun PlanMetric(label: String, value: String) {
    Div({
        style {
            textAlign("center")
            padding(8.px)
            backgroundColor(Color.white)
            borderRadius(6.px)
        }
    }) {
        Div({ style { fontSize(16.px); fontWeight("600"); color(Color("#333")); marginBottom(4.px) } }) {
            Text(value)
        }
        Div({ style { fontSize(12.px); color(Color("#999")) } }) {
            Text(label)
        }
    }
}

@Composable
private fun ReviewItem(label: String, value: String) {
    Div({
        style {
            marginBottom(12.px)
        }
    }) {
        Div({ style { fontSize(12.px); color(Color("#999")); marginBottom(4.px) } }) {
            Text(label)
        }
        Div({ style { fontSize(14.px); color(Color("#333")) } }) {
            Text(value)
        }
    }
}
